import { i18nKey, getTranslations } from './i18n-key';
import { fetchLanguage } from './translate';
import { getWesogUserInfo } from '@core/utils/wesog-user';
import { UPDATE_LANGUAGE_EVENT } from '@core/constants/EVENTS';
import { COMPANY_SHORT_NAME } from '@core/constants/COMPANY';
import { DATA_TRANSLATE_ATTRIBUTE } from '@core/constants/ATTRIBUTES';
import { getSelectedLanguage } from '@core/utils/language';
import { LANGUAGES } from '@core/constants/LANGUAGE';

/**
 * Update the UI of Web Components with translated strings.
 * @type {function}
 * @param {HTMLElement} elementsToTranslate - The elements to translate.
 * @returns {void}
 * @private
 */
const _updateWebComponentsUI = (elementsToTranslate) => {
    elementsToTranslate.forEach((element) => {
        /** @type {string} */
        const key = element.getAttribute(DATA_TRANSLATE_ATTRIBUTE);
        if (element.hasAttribute('label')) {
            element.setAttribute('label', translate(key));
        } else if (element.hasAttribute('data-translate-icon')) {
            element.innerHTML = translate(key);
        } else if (element.hasAttribute('placeholder')) {
            element.setAttribute('placeholder', translate(key));
        } else {
            element.textContent = translate(key);
        }
    });
};

/**
 * Update the UI with translated strings.
 * @type {function}
 * @returns {void}
 */
export const updateUI = () => {
    /** @type {NodeListOf<HTMLElement>|null} */
    const elementsToTranslate = document.querySelectorAll(
        `[${DATA_TRANSLATE_ATTRIBUTE}]`,
    );

    if (!elementsToTranslate) return;

    _updateWebComponentsUI(elementsToTranslate);
};

/**
 * Update the language data in the window object.
 * @type {function}
 * @param {string} selectedLanguage - The language to load.
 * @returns {Promise<void>}
 */
export const updateLoadLanguageData = (selectedLanguage) => {
    fetchLanguage(selectedLanguage);

    document.dispatchEvent(
        new CustomEvent(UPDATE_LANGUAGE_EVENT, {
            detail: {
                language: selectedLanguage,
            },
        }),
    );
};

/**
 * Translate a string based on the current language.
 * @type {function}
 * @param {string} key - The key to translate.
 * @returns {string} The translated string.
 */
export const translate = (key) => {
    /**
     * Split the key by '.' to navigate the nested structure.
     * @type {string[]}
     */
    const keys = key.split('.');

    /**
     * Start with the top-level language data.
     * @type {object}
     */
    let currentData = window.wesogTranslate;

    if (!currentData) {
        currentData = getTranslations();
    }

    /* Traverse the nested structure. */
    for (const nestedKey of keys) {
        currentData = currentData[nestedKey];
        if (!currentData) {
            /* If any part of the key is missing, return the key itself. */
            currentData = i18nKey(key);
        }
    }

    if (currentData.includes('%company')) {
        currentData = currentData.replace('%company', COMPANY_SHORT_NAME);
    }

    if (currentData.includes('%name')) {
        /** @type {UserInfo} */
        const user = getWesogUserInfo();
        currentData = currentData.replace('%name', user.username);

        const currentLanguage = getSelectedLanguage();
        if (currentLanguage === LANGUAGES.EN && user.username.endsWith('s')) {
            currentData = currentData.replace("'s", "'");
        }
    }

    return currentData;
};

/**
 * Update the UI with translated strings from the shadow root.
 * @type {function}
 * @param {ShadowRoot} shadowRoot - The shadow root to update.
 * @returns {void}
 */
export const updateUIFromShadowRoot = (shadowRoot) => {
    if (!shadowRoot) return;

    /** @type {NodeListOf<HTMLElement>|null} */
    const elementsToTranslate = shadowRoot.querySelectorAll(
        `[${DATA_TRANSLATE_ATTRIBUTE}]`,
    );

    if (!elementsToTranslate) return;

    _updateWebComponentsUI(elementsToTranslate);
    updateUI();
};

/**
 * Delay the translation of a string.
 * @param {function} fn - The function to execute after the delay.
 * @param {number} [delay] - The delay in milliseconds. Default is 300.
 * @returns {string} The translated string.
 */
export const delayTranslate = (fn, delay = 300) => {
    setTimeout(() => {
        fn();
    }, delay);
};
